import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import PageLayout from "../common/PageLayout";
import CustomPaper from "../common/CustomPaper";
import { viewportBelowPageHeader } from "../../constants/pageLayout";
import { send_request } from "../../utils/Request";
import AgentSessionsPanel from "./AgentSessionsPanel";
import AgentConfigDrawer from "./AgentConfigDrawer";
import useMasterAgentWorkspace from "./useMasterAgentWorkspace";
import CreditsUsedCard from "./HomePageComponents/CreditsUsedCard";
import AgentCardsSection from "./HomePageComponents/AgentCardsSection";
import RecentConversationsCard from "./HomePageComponents/RecentConversationsCard";
import RecentWorkflowsCompactCard from "./HomePageComponents/RecentWorkflowsCompactCard";

function HomeCardsPanel({ agents, agentsLoading }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ px: 2.25, pt: 2, pb: 1, flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "text.primary",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Overview
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: "text.secondary",
            mt: 0.75,
            lineHeight: 1.4,
          }}
        >
          Recent conversations, agents, usage, and workflows.
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        <AgentCardsSection agents={agents} loading={agentsLoading} compact />
        <RecentConversationsCard agents={agents} />
        <CreditsUsedCard />
        <RecentWorkflowsCompactCard />
      </Box>
    </Box>
  );
}

export default function HomePage() {
  const masterAgent = useMasterAgentWorkspace();
  const {
    agentId,
    agentName,
    agentDescription,
    agentModel,
    agentInboxAddress,
    examplePrompts,
    chatUiPolicy,
    loading,
    error,
    configOpen,
    setConfigOpen,
    taskQueueOpenSignal,
    openTaskQueue,
    taskQueueToggleVisible,
    taskQueuePanelOpen,
    sessionSidebarCollapsed,
    setSessionSidebarCollapsed,
    handleConfigSaved,
    handleTaskQueueToggleStateChange,
  } = masterAgent;
  const [displayName, setDisplayName] = useState("");
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      try {
        const json = await send_request("authz-service/user-info", "", {});
        if (cancelled || !json?.data) return;
        setDisplayName(json.data.displayName || "");
      } catch (e) {
        console.error("Error fetching user info:", e);
      }
    };

    const fetchAgents = async () => {
      try {
        const response = await send_request(
          "project-service/agent/list",
          "",
          {},
          "GET"
        );
        if (!cancelled) setAgents(response?.data || []);
      } catch (e) {
        console.error("Error fetching agents:", e);
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    };

    fetchUserInfo();
    fetchAgents();

    return () => {
      cancelled = true;
    };
  }, []);

  const title = displayName ? `Hello ${displayName}` : "Home";

  if (loading) {
    return (
      <PageLayout title={title} contentSx={{ px: 0, py: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} sx={{ color: "text.secondary" }} />
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title={title}>
        <Typography color="error">{error}</Typography>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={title}
      titleBarContent={
        <>
          <Box sx={{ flex: 1 }} />
          {taskQueueToggleVisible && !taskQueuePanelOpen && (
            <Tooltip title="Open task queue planner" arrow>
              <Button
                onClick={openTaskQueue}
                startIcon={<ChecklistOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{ textTransform: "none", color: "text.primary", mr: 1 }}
              >
                Task Queue
              </Button>
            </Tooltip>
          )}
        </>
      }
      contentSx={{ px: 0, py: 0 }}
    >
      <Box
        sx={{
          height: viewportBelowPageHeader(),
          display: "flex",
          minHeight: 0,
          overflow: "auto",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
          p: 2,
          bgcolor: "background.default",
        }}
      >
        <CustomPaper
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            minHeight: { xs: "70vh", lg: 0 },
            p: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <AgentSessionsPanel
            agentId={agentId}
            agentName={agentName}
            agentModel={agentModel}
            agentDescription={agentDescription}
            agentInboxAddress={agentInboxAddress}
            chatUiPolicy={chatUiPolicy}
            onConfigOpen={() => setConfigOpen(true)}
            showSessionSidebar
            taskQueueOpenSignal={taskQueueOpenSignal}
            onTaskQueueToggleStateChange={handleTaskQueueToggleStateChange}
            inlineTaskQueueToggle={false}
            sidebarCollapsed={sessionSidebarCollapsed}
            onSidebarCollapsedChange={setSessionSidebarCollapsed}
            examplePrompts={examplePrompts}
          />
        </CustomPaper>
        <CustomPaper
          sx={{
            width: { xs: "100%", lg: 420, xl: 460 },
            flex: "0 0 auto",
            minHeight: 0,
            p: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <HomeCardsPanel agents={agents} agentsLoading={agentsLoading} />
        </CustomPaper>
      </Box>
      <AgentConfigDrawer
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        agentId={agentId}
        onSaved={handleConfigSaved}
      />
    </PageLayout>
  );
}
